import { FC, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { Add, Undo } from '@carbon/react/icons';
import { SurveySection } from 'components/survey-description';
import { tcSelectedIsCloneAtom } from 'pages/type-curves/type-curve-filters/atoms';
import { UnitBuilderPaneHeader } from './unit-builder-pane-header';
import { formatNumber } from 'utils/number';
import { polygonAreaAcres } from 'utils/geometry-measure';
import { useUnitBuilder } from './context';
import { builderNameCheckAtom, savedDraftAtom } from './atoms';
import { unitPolygonFrom } from './unit-polygon';
import { useSurveyAreaSave } from './use-survey-area-save';
import { useSurveyRowEdit } from './use-survey-row-edit';
import style from './unit-builder-modal.module.scss';

/**
 * Survey Areas / Applicable Areas, beneath the map.
 *
 * Save creates and publishes the unit via `POST /area_geometries` (see
 * `useSurveyAreaSave`) — from the picked survey tracts, or the freehand drawn
 * ring when no tract is picked. It is enabled only when the unit is a clone or
 * the surveys were actually changed, and Undo continues to act on the boundary
 * the shell owns.
 *
 * An EXISTING (persisted) unit is read-only here: this Save only ever CREATEs, so
 * leaving it live would mint a duplicate area geometry under the same name. Add /
 * remove / search / Save are all disabled for it. Its rows stay empty (the picker
 * is never pre-filled from the unit — see `useSurveyAreaSave`), so its acreage is
 * measured from the boundary rather than previewed from survey rows.
 *
 * @param {UnitBuilderSurveyAreasProps} props - Server acreage of the composed boundary.
 * @return {JSX.Element} The survey-areas panel.
 */
interface UnitBuilderSurveyAreasProps {
  /**
   * Acreage of the composed survey union as PostGIS measures it — authoritative
   * (and quarter-call-aware), unlike the client estimate. Null until previewed.
   */
  grossAcres?: number | null;
}

export const UnitBuilderSurveyAreas: FC<UnitBuilderSurveyAreasProps> = ({
  grossAcres = null,
}) => {
  const { shapeEdit, unitName, unitId, unitAcreage, baseRing } =
    useUnitBuilder();
  const survey = useSurveyAreaSave(unitId, unitName, shapeEdit.pieces);
  const isClone = useAtomValue(tcSelectedIsCloneAtom);
  const savedDraft = useAtomValue(savedDraftAtom);
  const editRows = useSurveyRowEdit();

  // Save creates/publishes the unit via POST /area_geometries. Enable it in the
  // two cases the unit can be committed: this is a clone (a new copy to persist),
  // or the user actually changed the surveys — provided there is a shape to save
  // (a picked tract or a drawn ring).
  const hasSource =
    survey.rows.some((row) => row.location) ||
    shapeEdit.pieces.some((piece) => piece.length >= 3);
  // A persisted unit is read-only (see the note above), so it can never save.
  const isExistingUnit = unitId !== null;
  // The unit cannot be created under a name the server will refuse, so Save waits
  // on the dry run's verdict. `errors` carries shape refusals too, so only the
  // name ones count here — a non-contiguous boundary is a different problem and
  // blocks Save on its own terms.
  const nameCheck = useAtomValue(builderNameCheckAtom);
  const nameRejected = nameCheck.errors.some((message) =>
    /\bname\b/i.test(message)
  );
  const nameReady = !nameCheck.loading && !nameRejected;
  // `savedDraft` is a guard, not a second source of truth for "existing": the
  // read-only behaviour all derives from `unitId`, which only turns real once the
  // refetched unit list reveals the new unit's id. This closes the window until
  // then — and permanently if adoption never lands — so Save cannot create the
  // same unit twice. Needed because `isClone` short-circuits the baseline check.
  // One polygon or nothing: `ringCount > 1` means the boundary is disjoint shapes
  // that would not merge, which the warning below the actions explains.
  const { ringCount } = useMemo(
    () => unitPolygonFrom(shapeEdit.pieces),
    [shapeEdit.pieces]
  );

  const canSave =
    ringCount === 1 &&
    !isExistingUnit &&
    !savedDraft &&
    !survey.saving &&
    hasSource &&
    nameReady &&
    (isClone || survey.surveysUpdated);

  // Measure the working boundary with the SAME util the Create New Area panel uses
  // for its drawn polygons. `shapeEdit.pieces` IS the composed boundary now
  // (`composeUnitBoundary` in the map), so there is nothing to re-derive here; the
  // pieces are disjoint merged rings, so their areas add. (The mock measures only
  // its largest piece, which under-reports a multi-piece unit.)
  const measuredAcres = useMemo(() => {
    if (shapeEdit.pieces.length === 0) return null;
    const total = shapeEdit.pieces.reduce(
      (sum, ring) =>
        sum + polygonAreaAcres({ type: 'Polygon', coordinates: [ring] }),
      0
    );
    return total > 0 ? total : null;
  }, [shapeEdit.pieces]);

  // A unit with survey areas picked on top of it: the boundary is the UNION of the
  // two, and no server figure describes that shape — `grossAcres` is the preview's
  // acreage for the tracts alone, so it under-reports. Measured on the client, to
  // avoid a second chained preview round-trip per edit.
  //
  // Drafts only. A persisted unit's rows are seeded FROM the unit, so this would
  // otherwise read as "merged" for every existing unit and replace its DB acreage
  // with a client measurement.
  const isMergedShape =
    !isExistingUnit && baseRing !== null && survey.rows.some((r) => r.location);

  // Otherwise, most authoritative first:
  //   1. the preview's server acreage of the composed survey union (draft built
  //      from surveys only — the shape the preview actually measured),
  //   2. the unit's own stored GIS acreage, straight off the payload the boundary
  //      read already fetches — the same figure the development-area page shows as
  //      "Gross Acres (GIS)" (for a clone, its source unit's). The client
  //      measurement disagrees with it (968 vs 965.26 on U-0000083642) because it
  //      is only a display approximation, so the DB value wins — but only while
  //      the boundary is UNCHANGED, since it describes the saved shape.
  //   3. the client measurement, for a draft with no server figure yet (the
  //      `drawn` preview branch returns geometry without acreage).
  const acres = isMergedShape
    ? measuredAcres
    : (grossAcres ??
      (!shapeEdit.dirty && unitAcreage !== null ? unitAcreage : measuredAcres));

  return (
    <div className={`${style.pane} ${style.paneUnclipped}`}>
      <UnitBuilderPaneHeader
        title="Survey Areas / Applicable Areas"
        trailing={
          acres !== null
            ? `${formatNumber({ value: acres, minPrecision: 0 })} ac`
            : undefined
        }
      />
      <div className={style.surveyAreas}>
        {survey.savedBanner && (
          <InlineNotification
            kind="success"
            lowContrast
            className={style.savedBanner}
            title={`Unit ${survey.savedBanner} shape updated`}
            onCloseButtonClick={survey.clearBanner}
          />
        )}

        {/* Add sits left of Undo / Save, as the mock has it. */}
        <div className={style.surveyActions}>
          <Button
            kind="primary"
            size="sm"
            renderIcon={Add}
            disabled={isExistingUnit}
            onClick={survey.addRow}
          >
            Add survey area
          </Button>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Undo}
            disabled={!shapeEdit.canUndo}
            onClick={shapeEdit.undo}
          >
            Undo
          </Button>
          <Button
            kind="tertiary"
            size="sm"
            disabled={!canSave}
            onClick={() => void survey.save()}
          >
            {survey.saving ? <InlineLoading description="Saving..." /> : 'Save'}
          </Button>
        </div>

        {ringCount > 1 && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={style.shapeWarning}
            title="This unit is more than one shape"
            subtitle="A unit has to be a single polygon. Shapes that touch are merged automatically, so remove or reshape the part that stands apart — usually a survey area that does not adjoin the unit."
          />
        )}

        <SurveySection
          rows={survey.rows}
          // Every edit here — a picked area, a removed row, a quarter call — is
          // one undoable action.
          onChange={editRows}
          title=""
          hint=""
          idPrefix="unit-builder-survey"
          hideAddButton
          // Survey options are fetched only once the operator types (2 chars),
          // rather than on first focus — no request just for opening the builder.
          searchMinChars={2}
          disabled={isExistingUnit}
        />
      </div>
    </div>
  );
};
